import React, { useState, useEffect, useCallback } from "react";
import { adminLiquidityApi } from "../../services/api";

const INCIDENT_TYPES = [
  { value: "", label: "All types" },
  { value: "WALLET_LIMIT", label: "WALLET_LIMIT" },
  { value: "WEEKLY_WARNING", label: "WEEKLY_WARNING" },
  { value: "WEEKLY_CRITICAL", label: "WEEKLY_CRITICAL" },
];

const INCIDENT_COLORS = {
  WALLET_LIMIT: { bg: "#fff5f5", color: "#c53030" },
  WEEKLY_WARNING: { bg: "#fffaf0", color: "#c05621" },
  WEEKLY_CRITICAL: { bg: "#fff5f5", color: "#822727" },
};

/**
 * Tab Incidenti del pannello admin liquidità.
 */
const AdminLiquidityIncidents = ({ adminKey }) => {
  const [incidents, setIncidents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [filterResolved, setFilterResolved] = useState("false");
  const [resolvingId, setResolvingId] = useState(null);
  const PAGE_SIZE = 20;
  const [offset, setOffset] = useState(0);

  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: PAGE_SIZE, offset };
      if (filterType) params.type = filterType;
      if (filterResolved !== "") params.resolved = filterResolved;
      const data = await adminLiquidityApi.getIncidents(adminKey, params);
      setIncidents(data.incidents || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message || "Error loading incidents");
    } finally {
      setLoading(false);
    }
  }, [adminKey, filterType, filterResolved, offset]);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  // Reset offset when filters change
  useEffect(() => {
    setOffset(0);
  }, [filterType, filterResolved]);

  const handleResolve = async (id) => {
    if (!window.confirm(`Mark incident #${id} as resolved?`)) return;
    setResolvingId(id);
    try {
      await adminLiquidityApi.resolveIncident(adminKey, id);
      await fetchIncidents();
    } catch (e) {
      alert("Errore: " + e.message);
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <div style={s.container}>
      {/* Filtri */}
      <div style={s.filtersRow}>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          style={s.select}
        >
          {INCIDENT_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>

        <select
          value={filterResolved}
          onChange={(e) => setFilterResolved(e.target.value)}
          style={s.select}
        >
          <option value="">All statuses</option>
          <option value="false">Open</option>
          <option value="true">Resolved</option>
        </select>

        <button onClick={fetchIncidents} style={s.btnRefresh}>
          🔄 Refresh
        </button>

        <span style={s.totalLabel}>Total: {total}</span>
      </div>

      {error && <div style={s.error}>⚠️ {error}</div>}

      {loading ? (
        <div style={s.loading}>Loading incidents...</div>
      ) : incidents.length === 0 ? (
        <div style={s.empty}>✅ No incidents to display.</div>
      ) : (
        <div style={s.tableWrapper}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>ID</th>
                <th style={s.th}>Tipo</th>
                <th style={s.th}>Wallet</th>
                <th style={s.th}>ETH</th>
                <th style={s.th}>Data</th>
                <th style={s.th}>Stato</th>
                <th style={s.th}>Azione</th>
              </tr>
            </thead>
            <tbody>
              {incidents.map((inc) => {
                const colors = INCIDENT_COLORS[inc.incident_type] || {};
                return (
                  <tr key={inc.id} style={{ backgroundColor: colors.bg || "white" }}>
                    <td style={s.td}>{inc.id}</td>
                    <td style={{ ...s.td, color: colors.color, fontWeight: 600, fontSize: 12 }}>
                      {inc.incident_type}
                    </td>
                    <td style={{ ...s.td, fontFamily: "monospace", fontSize: 12 }}>
                      {inc.wallet
                        ? `${inc.wallet.slice(0, 8)}...${inc.wallet.slice(-6)}`
                        : "—"}
                    </td>
                    <td style={s.td}>
                      {inc.eth_amount
                        ? `${parseFloat(inc.eth_amount).toFixed(4)} ETH`
                        : inc.weekly_total
                        ? `${parseFloat(inc.weekly_total).toFixed(4)} ETH`
                        : "—"}
                    </td>
                    <td style={{ ...s.td, fontSize: 12 }}>
                      {inc.created_at
                        ? new Date(inc.created_at).toLocaleString("en-US")
                        : "—"}
                    </td>
                    <td style={s.td}>
                      {inc.resolved ? (
                        <span style={s.badgeGreen}>✓ Resolved</span>
                      ) : (
                        <span style={s.badgeRed}>Open</span>
                      )}
                    </td>
                    <td style={s.td}>
                      {!inc.resolved && (
                        <button
                          onClick={() => handleResolve(inc.id)}
                          disabled={resolvingId === inc.id}
                          style={{ ...s.btnSmall, opacity: resolvingId === inc.id ? 0.6 : 1 }}
                        >
                          {resolvingId === inc.id ? "..." : "✓ Resolve"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginazione */}
      {total > PAGE_SIZE && (
        <div style={s.pagination}>
          <button
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0}
            style={s.btnPage}
          >
            ← Previous
          </button>
          <span style={s.pageInfo}>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <button
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={offset + PAGE_SIZE >= total}
            style={s.btnPage}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

const s = {
  container: { padding: "0 4px" },
  filtersRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  select: {
    padding: "7px 10px",
    border: "1px solid #cbd5e0",
    borderRadius: 4,
    fontSize: 14,
    backgroundColor: "white",
    color: "#2c3e50",
  },
  btnRefresh: {
    padding: "7px 12px",
    backgroundColor: "#edf2f7",
    border: "1px solid #cbd5e0",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 13,
  },
  totalLabel: { marginLeft: "auto", fontSize: 13, color: "#718096" },
  loading: { padding: 24, color: "#718096", textAlign: "center" },
  error: {
    padding: 12,
    color: "#c53030",
    backgroundColor: "#fff5f5",
    borderRadius: 6,
    marginBottom: 12,
  },
  empty: { padding: 24, color: "#38a169", textAlign: "center" },
  tableWrapper: { overflowX: "auto" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: "white",
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    fontSize: 14,
  },
  th: {
    padding: "10px 12px",
    backgroundColor: "#2c3e50",
    color: "white",
    fontWeight: 600,
    textAlign: "left",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "9px 12px",
    borderBottom: "1px solid #edf2f7",
    verticalAlign: "middle",
  },
  badgeGreen: {
    backgroundColor: "#c6f6d5",
    color: "#276749",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
  },
  badgeRed: {
    backgroundColor: "#fed7d7",
    color: "#c53030",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
  },
  btnSmall: {
    padding: "4px 10px",
    backgroundColor: "#27ae60",
    color: "white",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 500,
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    marginTop: 16,
  },
  btnPage: {
    padding: "6px 14px",
    backgroundColor: "#edf2f7",
    border: "1px solid #cbd5e0",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 13,
  },
  pageInfo: { fontSize: 13, color: "#718096" },
};

export default AdminLiquidityIncidents;
